/**
 * SR Cart AI Recommendations
 * Replaces static collection-based upsells with AI-driven "Komplettera din rutin".
 * Uses /similar endpoint with cart handles + routine mode.
 */
(function () {
  'use strict';
  if (window.__srCartRecoInit) return;
  window.__srCartRecoInit = true;

  var API = (window.SR_SEARCH_API || 'https://skinroller-search.skinroller.workers.dev').replace(/\/$/, '');

  // Curated accessory pairs: cart product handle -> companion handle.
  // A matched companion always takes the first card; AI fills the rest.
  var PAIRS = window.SR_RECO_PAIRS || {
    'brilliant-purifying-pink-clay-mask-100-ml': 'skinroller-spatel-ansiktsmask',
    'djuprengorande-ansiktsmask-mot-finnar': 'skinroller-spatel-ansiktsmask'
  };


  // type:-tag -> Swedish eyebrow label
  var TYPE_LABELS = {
    'cleanser': 'Ansiktsrengöring', 'toner': 'Ansiktsvatten', 'serum': 'Serum',
    'oil': 'Olja', 'cream': 'Ansiktskräm', 'mask': 'Ansiktsmask',
    'sheet-mask': 'Sheet mask', 'exfoliant': 'Peeling', 'sunscreen': 'Solskydd',
    'mist': 'Mist', 'eye-care': 'Ögonvård', 'tool': 'Tillbehör',
    'dermaroller': 'Dermaroller', 'kit': 'Kit', 'supplement': 'Kosttillskott'
  };
  // product_type is junk when it's really the brand (Skinroller, Neutriherbs, ...)
  function isJunkType(type, brand) {
    if (!type) return true;
    if (brand && type.toLowerCase() === String(brand).toLowerCase()) return true;
    return ['skinroller', 'neutriherbs', 'cicamed', 'chamos acaci', 'blansen', 'xlash', 'herbique',
            'alla produkter', 'produkter', 'hudvard', 'hudvård'].indexOf(type.toLowerCase()) !== -1;
  }
  function typeFromTags(tags) {
    if (!tags) return null;
    for (var i = 0; i < tags.length; i++) {
      if (tags[i].indexOf('type:') === 0) {
        var slug = tags[i].slice(5);
        return TYPE_LABELS[slug] || (slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' '));
      }
    }
    return null;
  }
  // Always-on low-ticket impulse item, pinned at slot 2 (index 1).
  // Skipped automatically if it's already in the cart or unavailable.
  // variant_id pekar ut EN bestämd variant. Utan den väljs första tillgängliga,
  // vilket för flerpacksprodukter är 1-styck — och en 1-styck under ~48 kr bär
  // inte den förlorade fraktintäkten när den tippar ordern över fri frakt-gränsen.
  var PINNED = window.SR_RECO_PINNED || {
    handle: 'vitamin-c-sheet-mask-koreansk-ansiktsmask',
    variant_id: 52720577020246,
    position: 1
  };

  // Fetch storefront product JSON and shape it like a worker result
  function fetchCompanion(handle, variantId) {
    return fetchProductFull(handle)
      .then(function (p) {
        var v = null;
        if (variantId) {
          v = p.variants.find(function (x) { return x.id === variantId && x.available; });
        }
        if (!v) v = p.variants.find(function (x) { return x.available; });
        if (!v) return null;
        variantCache[handle] = v.id;
        return {
          handle: p.handle,
          name: p.title,
          // Visas efter produktnamnet när produkten har flera varianter, så att
          // kortet säger "3 st" i stället för att se ut som lösvikt.
          variant_label: p.variants.length > 1 ? v.title : null,
          product_type: typeFromTags(p.tags) || (isJunkType(p.type, p.vendor) ? null : p.type),
          price: v.price / 100,
          compare_at_price: v.compare_at_price ? v.compare_at_price / 100 : null,
          // Ra featured_image ar originalfilen (kan vara 300+ KB) - de
          // worker-hamtade korten far fardigskalade bilder, sa utan
          // transformen blev pinned-kortet en vit ruta pa langsam lina
          // tills originalet laddat. 240 px racker for 56px-rutan pa retina.
          image_url: p.featured_image
            ? p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&width=240' : '?width=240')
            : null,
          in_stock: true
        };
      })
      .catch(function () { return null; });
  }

  var productFullCache = {};
  function fetchProductFull(handle) {
    if (!productFullCache[handle]) {
      productFullCache[handle] = fetch('/products/' + handle + '.js')
        .then(function (r) {
          if (!r.ok) throw new Error('product ' + r.status);
          return r.json();
        })
        .catch(function (err) {
          delete productFullCache[handle];
          throw err;
        });
    }
    return productFullCache[handle];
  }

  var PRODUCT_TTL = 10 * 60 * 1000;
  function fetchProductJson(handle) {
    var key = 'sr-reco-p:' + handle;
    try {
      var stored = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (stored && Date.now() - stored.t < PRODUCT_TTL) return Promise.resolve(stored.p);
    } catch (e) {}
    return fetchProductFull(handle).then(function (full) {
      var slim = {
        tags: full.tags,
        type: full.type,
        vendor: full.vendor,
        variants: full.variants.map(function (v) { return { id: v.id, available: v.available }; })
      };
      try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), p: slim })); } catch (e) {}
      return slim;
    });
  }

  // --- GET CART HANDLES ---
  function getCartHandles() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var handles = [];
        cart.items.forEach(function (item) {
          if (item.handle && handles.indexOf(item.handle) === -1) {
            handles.push(item.handle);
          }
        });
        return handles;
      });
  }

  // --- FETCH RECOMMENDATIONS ---
  var recosCache = {};
  function fetchRecos(handles) {
    if (!handles || handles.length === 0) return Promise.resolve(null);
    var key = handles.slice().sort().join(',');
    var hit = recosCache[key];
    if (!hit || Date.now() - hit.t > 5 * 60 * 1000) {
      hit = recosCache[key] = {
        t: Date.now(),
        p: fetch(API + '/similar?handles=' + encodeURIComponent(handles.join(',')) + '&mode=routine&limit=8')
          .then(function (r) {
            if (!r.ok) throw new Error('similar ' + r.status);
            return r.json();
          })
          .catch(function (err) {
            delete recosCache[key];
            throw err;
          })
      };
    }
    return hit.p.then(function (data) { return JSON.parse(JSON.stringify(data)); });
  }

  // --- PRE-CACHE VARIANT IDS ---
  var variantCache = {};
  // Senast renderade upsell-HTML. Body-swapparna tommer containern; cachen
  // satts tillbaka synkront sa upsellen star kvar medan farsk data hamtas.
  var lastRecoHTML = '';
  var lastRecoSig = '';

  // Delegerad klicklyssnare - EN per containernod (flaggan foljer noden och
  // nollstalls darmed automatiskt nar en swap ersatter containern).
  function bindRecoClicks(container) {
    if (container.__srRecoBound) return;
    container.__srRecoBound = true;
    container.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-sr-cart-add]');
      if (!btn || !container.contains(btn)) return;
      e.preventDefault();
      // Stop the bubble: the theme's outside-click guard sees our swapped-out
      // icon as a detached node and would close the drawer otherwise.
      e.stopPropagation();
      if (btn.disabled || btn.classList.contains('is-added')) return;
      addToCart(btn.dataset.srCartAdd, btn);
    });
  }

  function precacheVariants(handles) {
    handles.forEach(function (handle) {
      if (variantCache[handle]) return;
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          var variant = product.variants.find(function (v) { return v.available; }) || product.variants[0];
          if (variant) variantCache[handle] = variant.id;
        })
        .catch(function () {});
    });
  }

  // --- ADD TO CART ---
  function addToCart(handle, el) {
    // Find the actual button in this card for optimistic UI
    var card = el.closest('.cart-upsell-card') || el.closest('.cart-upsell-item');
    var btn = card ? card.querySelector('[data-sr-cart-add]') : el;
    btn.disabled = true;
    btn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
    btn.classList.add('is-added');

    var doAdd = function (variantId) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then(function () {
          // Track (fire & forget)
          fetch(API + '/event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ search_id: 'cart_reco', event_type: 'add_to_cart', handle: handle }),
          }).catch(function () {});

          // Ingen egen refresh har: cart-add-intercept fangar /cart/add
          // och koar omritningen via SRCartRefresh. Ett eget anrop till
          // skulle bara stalla en andra, identisk swap i kon.
        })
        .catch(function () {
          btn.disabled = false;
          btn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
          btn.classList.remove('is-added');
        });
    };

    // Use cached variant or fetch it
    if (variantCache[handle]) {
      doAdd(variantCache[handle]);
    } else {
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          var variant = product.variants.find(function (v) { return v.available; }) || product.variants[0];
          variantCache[handle] = variant.id;
          doAdd(variant.id);
        })
        .catch(function () {
          btn.disabled = false;
          btn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
          btn.classList.remove('is-added');
        });
    }
  }

  // --- RENDER ---
  function renderRecos(container, data) {
    if (!data || !data.results || data.results.length === 0) return;

    // Idempotens: samma resultat som redan star i containern -> ror inte
    // DOM:en. Varje innerHTML-skrivning skapar nya <img>-noder som mobil-
    // Safari ritar vita tills de laddats om - det ar flimret. Kedjan efter
    // en swap (cache-aterstallning + farsk render med samma data) och varje
    // ladoppning traffade annars alltid en onodig omskrivning.
    var sig = data.results.map(function (p) {
      return p.handle + ':' + p.price + ':' + (p.compare_at_price || '');
    }).join('|');
    if (sig === container.__srRecoSig && container.querySelector('.cart-upsell-grid')) {
      bindRecoClicks(container);
      return;
    }
    container.__srRecoSig = sig;
    lastRecoSig = sig;

    var collapsed = false;
    try { collapsed = window.localStorage.getItem('sr_upsell_collapsed') === '1'; } catch (e) {}
    var h = '<details class="cart-upsell-details"' + (collapsed ? '' : ' open') + '>';
    h += '<summary class="cart--drawer-suggestion-header">';
    h += '<h6 class="cart--drawer-suggestion-heading heading-font">Komplettera din rutin</h6>';
    h += '<svg class="cart-upsell-details__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
    h += '</summary>';
    h += '<p class="cart--drawer-suggestion-sub">Produkter som kompletterar din rutin för maximal effekt</p>';
    h += '<div class="cart-upsell-grid">';

    data.results.forEach(function (p) {
      var priceHtml = '';
      if (p.compare_at_price && p.compare_at_price > p.price) {
        priceHtml = '<span class="product--actual-price price-sale">' + Math.round(p.price) + ' kr</span>' +
          '<span class="product--cut-price line-through">' + Math.round(p.compare_at_price) + ' kr</span>';
      } else {
        priceHtml = '<span class="product--actual-price">' + Math.round(p.price) + ' kr</span>';
      }

      var nameParts = p.name.split(' - ');
      var mainName = nameParts[0];
      var subName = nameParts.slice(1).join(' - ');
      // Short category labels only — no post-dash fallback (mixed lengths looked buggy)
      var eyebrow = isJunkType(p.product_type, p.brand) ? '' : p.product_type;

      h += '<div class="cart-upsell-card">';
      h += '<div class="cart-upsell-card__media">';
      h += '<a href="/products/' + p.handle + '">';
      if (p.image_url) {
        h += '<img src="' + p.image_url + '" alt="' + esc(p.name) + '" loading="lazy">';
      }
      h += '</a>';
      h += '<button class="cart-upsell-card__add" data-sr-cart-add="' + p.handle + '" aria-label="Lägg till ' + esc(mainName) + '">';
      h += '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      h += '</button>';
      h += '</div>';
      h += '<span class="cart-upsell-item__eyebrow">' + (eyebrow ? esc(eyebrow) : '&nbsp;') + '</span>';
      var titleHtml = esc(mainName) + (p.variant_label ? ' <span class="cart-upsell-card__variant">· ' + esc(p.variant_label) + '</span>' : '');
      h += '<a href="/products/' + p.handle + (p.variant_label ? '?variant=' + variantCache[p.handle] : '') + '" class="cart-upsell-card__title">' + titleHtml + '</a>';
      h += '<div class="cart-upsell-card__price">' + priceHtml + '</div>';
      h += '</div>';
    });

    h += '</div>';
    h += '</details>';
    container.innerHTML = h;
    lastRecoHTML = h;

    // Desktop-tvakolumnslayouten i cart.css nycklas pa .has-ai-upsell har.
    // Tidigare nycklades den pa :has(.cart-upsell-details) - en nod som
    // forsvinner vid varje body-swap, sa ladan flippade grid->flex->grid
    // och footern hoppade. Klassen satts en gang pa <cart-drawer> (som
    // overlever partiella swappar) och tas aldrig bort.
    var drawerRoot = container.closest('cart-drawer') || document.querySelector('cart-drawer');
    if (drawerRoot) drawerRoot.classList.add('has-ai-upsell');

    // Remember collapsed state across re-renders and page loads
    var details = container.querySelector('.cart-upsell-details');
    if (details) {
      details.addEventListener('toggle', function () {
        try { window.localStorage.setItem('sr_upsell_collapsed', details.open ? '0' : '1'); } catch (e) {}
      });
    }

    bindRecoClicks(container);

    // Pre-cache variant IDs for instant ATC
    precacheVariants(data.results.map(function (p) { return p.handle; }));

    // Track impressions - EN gang per unik resultatuppsattning och sidladdning.
    // Utan dedupen postades en impression vid varje rendering (varje ladoppning,
    // varje swap), vilket bade spammade konsolen via interceptens fetch-stack
    // och fick workern att rate-limita klienten.
    var impressionSig = data.results.map(function (r) { return r.handle; }).join(',');
    if (impressionSig !== renderRecos.__lastImpression) {
      renderRecos.__lastImpression = impressionSig;
      fetch(API + '/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ search_id: 'cart_reco', event_type: 'click', handle: 'impression:' + impressionSig }),
      }).catch(function () {});
    }
  }

  // --- MAIN LOAD ---
  var recoInFlight = false;
  var recoQueued = false;
  var recoDirty = true;
  document.addEventListener('sr:cart-mutated', function () { recoDirty = true; });

  function loadRecos() {
    var containers = document.querySelectorAll('.cart--drawer-suggestions');
    if (!containers.length) return;

    // loadRecos kan triggas av flera hall nara i tid (drawer-oppning +
    // body-swap-observern). Utan sparr hamtade tva korningar fran workern,
    // filtrerade mot olika ogonblicksbilder av vagnen och renderade over
    // varandra - darav det vaxlande kortantalet. Den andra korningen koas
    // och kor en gang nar den forsta ar klar.
    if (recoInFlight) { recoQueued = true; return; }
    if (!recoDirty && lastRecoHTML) {
      containers.forEach(function (c) {
        if (c.childElementCount === 0) {
          c.innerHTML = lastRecoHTML;
          c.__srRecoSig = lastRecoSig;
          bindRecoClicks(c);
        }
      });
      return;
    }
    recoDirty = false;
    recoInFlight = true;
    var release = function () {
      recoInFlight = false;
      if (recoQueued) { recoQueued = false; setTimeout(loadRecos, 0); }
    };

    return getCartHandles()
      .then(function (handles) {
        if (handles.length === 0) {
          containers.forEach(function(c) { c.style.display = 'none'; });
          return;
        }
        containers.forEach(function(c) { c.style.display = ''; });

        // Curated pair: first cart product with a defined companion not already in cart
        var companionHandle = null;
        for (var i = 0; i < handles.length; i++) {
          var cand = PAIRS[handles[i]];
          if (cand && handles.indexOf(cand) === -1) { companionHandle = cand; break; }
        }

        var pinnedHandle = PINNED && PINNED.handle && handles.indexOf(PINNED.handle) === -1 ? PINNED.handle : null;
        return Promise.all([
          fetchRecos(handles),
          companionHandle ? fetchCompanion(companionHandle) : Promise.resolve(null),
          pinnedHandle ? fetchCompanion(pinnedHandle, PINNED.variant_id) : Promise.resolve(null)
        ]).then(function (res) {
          var data = res[0], companion = res[1], pinned = res[2];
          if (data && data.results) {
            var EXCLUDE = window.SR_RECO_EXCLUDE || [
              'rengoringsspray-10-ml',
              'clean-dermaroller-rengoring-50ml',
              'face-mist-bamboo-aqua-120-ml-koreansk-ansiktsvatten',
              'koreansk-ansiktskram-travel-size',
              'lemon-ultra-whitening-cream-travel-size',
              'vitamin-c-sheet-mask-1-st-1-st'
            ];
            data.results = data.results.filter(function (p) {
              return EXCLUDE.indexOf(p.handle) === -1 && handles.indexOf(p.handle) === -1;
            });
            if (companion) {
              data.results = [companion].concat(data.results.filter(function (p) {
                return p.handle !== companion.handle;
              }));
              data._pairMatched = true;
            }
            // Final count decided after type-dedupe below
          }
          var gotResults = data && data.results && data.results.length > 0;
          document.querySelectorAll('[data-static-upsell]').forEach(function (el) {
            el.style.display = gotResults ? 'none' : '';
          });
          if (!gotResults) return;
          var withTypes = data.results.map(function (p) {
            // The worker echoes Shopify's junk product_type ('Skinroller') —
            // only trust an existing value that isn't that; otherwise resolve
            // the real category from the type: tags.
            if (!isJunkType(p.product_type, p.brand)) return Promise.resolve(p);
            return fetchProductJson(p.handle)
              .then(function (full) {
                p.product_type = typeFromTags(full.tags) || (isJunkType(full.type, full.vendor) ? null : full.type);
                // Skriv aldrig över en variant som PINNED pekat ut. Returnerar
                // workern samma handle hamnar den här innan pinned-filtret
                // hunnit ta bort den, och skulle annars byta 3-packet mot
                // första tillgängliga variant (1 st).
                var isPinned = PINNED && PINNED.variant_id && p.handle === PINNED.handle;
                if (!isPinned) {
                  var v = full.variants.find(function (x) { return x.available; }) || full.variants[0];
                  if (v) variantCache[p.handle] = v.id;
                }
                return p;
              })
              .catch(function () { return p; });
          });
          if (pinned) {
            data.results = data.results.filter(function (p) { return p.handle !== pinned.handle; });
            var pos = Math.min(PINNED.position || 1, data.results.length);
            data.results.splice(pos, 0, pinned);
          }
          return Promise.all(withTypes).then(function () {
            // Max one card per product type (first occurrence wins; pinned outranks AI)
            var preDedupe = data.results.slice();
            var seenTypes = {};
            data.results = data.results.filter(function (p) {
              var key = p.product_type || 'okänd:' + p.handle;
              if (seenTypes[key]) return false;
              seenTypes[key] = true;
              return true;
            });
            var RECO_LIMIT = window.SR_RECO_LIMIT || 6;
            var RECO_MIN = window.SR_RECO_MIN || 4;
            if (data.results.length < RECO_MIN) {
              var chosen = {};
              data.results.forEach(function (p) { chosen[p.handle] = true; });
              for (var t = 0; t < preDedupe.length && data.results.length < RECO_MIN; t++) {
                var cand = preDedupe[t];
                if (chosen[cand.handle]) continue;
                chosen[cand.handle] = true;
                data.results.push(cand);
              }
            }
            data.results = data.results.slice(0, RECO_LIMIT);
            if (data.results.length === 0) {
              document.querySelectorAll('[data-static-upsell]').forEach(function (el) { el.style.display = ''; });
              return;
            }
            if (data.results.length < RECO_MIN) {
              document.querySelectorAll('[data-static-upsell]').forEach(function (el) { el.style.display = ''; });
            }
            // Re-query fresh: a cart-page section swap may have replaced the
            // original container node while the worker fetch was in flight —
            // render into the live node, never the detached one.
            var live = document.querySelectorAll('.cart--drawer-suggestions');
            (live.length ? live : containers).forEach(function(c) { renderRecos(c, data); });
          });
        });
      })
      .catch(function () {
        recoDirty = true;
        // Worker unreachable — reveal the static Liquid fallback slider
        document.querySelectorAll('[data-static-upsell]').forEach(function (el) { el.style.display = ''; });
      })
      .then(release, release);
  }

  // --- OBSERVE CART DRAWER OPEN + CART PAGE ---
  function init() {
    // Drawer: observe open/close
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      new MutationObserver(function () {
        if (cartDrawer.classList.contains('show') || cartDrawer.classList.contains('active')) {
          loadRecos();
        }
      }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });

      // Any re-render of the drawer body (theme ajax, qty updates) resets the
      // upsell area — debounce-reload the AI recos so the static fallback
      // never lingers next to them.
      var bodyEl = cartDrawer.querySelector('[data-cart-drawer-body]');
      if (bodyEl) {
        var recoTimer = null;
        new MutationObserver(function () {
          var suggestions = cartDrawer.querySelector('.cart--drawer-suggestions');
          if (suggestions && suggestions.childElementCount > 0) return; // our own render
          // Swappen tomde containern. Satt tillbaka forra renderingen direkt
          // sa kunden aldrig ser en tom ruta; loadRecos ersatter med farsk
          // data strax efter. Delegeringsflaggan foljde den gamla noden,
          // sa klicken binds om av renderRecos nar den kor.
          if (suggestions && lastRecoHTML) {
            suggestions.innerHTML = lastRecoHTML;
            // Stampla signaturen pa den nya containern: kommer farska datan
            // tillbaka oforandrad hoppar renderRecos over omskrivningen och
            // bilderna lamnas i fred.
            suggestions.__srRecoSig = lastRecoSig;
            bindRecoClicks(suggestions);
          }
          clearTimeout(recoTimer);
          recoTimer = setTimeout(loadRecos, 250);
        }).observe(bodyEl, { childList: true });
      }
    }

    // Cart page: load immediately
    var cartPage = document.querySelector('.main-cart--form');
    if (cartPage) {
      loadRecos();

      // Re-render safety: adding/removing/qty-changing an item makes the theme
      // re-render the whole cart section, which wipes the AI upsell. Watch a
      // persistent ancestor and re-run loadRecos whenever the suggestions area
      // is emptied by such a re-render (debounced; cheap no-op once populated).
      var pageRoot = document.querySelector('.main-cart--outer') ||
                     document.querySelector('.main-cart--content') || document.body;
      var pageRecoTimer = null;
      new MutationObserver(function () {
        var s = document.querySelector('.cart--drawer-suggestions');
        if (s && s.childElementCount > 0) return;      // already rendered — our own output
        if (!document.querySelector('.main-cart--form')) return; // cart emptied
        clearTimeout(pageRecoTimer);
        pageRecoTimer = setTimeout(loadRecos, 250);
      }).observe(pageRoot, { childList: true, subtree: true });
    }

    console.log('[SR] Cart AI Reco ready');
  }

  function esc(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
